import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { buildDependencyGraph } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { determineOptionsAvailable } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { sortFilterValues } from 'libs/end-to-end/src/sorting';
import { z } from 'zod';
import { useSessionStorage } from 'apps/sigma2-ui/src/lib/hooks/useSessionStorage';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { FilterInformation } from 'apps/sigma2-ui/src/lib/hooks/filter-state';
import { FiltersState } from 'apps/sigma2-ui/src/lib/hooks/filter-state';

/**
 * @example { department: { displayName: 'Department', values: ['Sales', 'Engineering'] } }
 */

type OnSelectParams = {
  columnHeaderValue: string;
  selectedValues: string[];
};

export interface OnSelect {
  (params: OnSelectParams): FilterInformation | null;
}

export interface OnInject {
  (params: { columnHeaderValue: string; values: string[] }[]): void;
}

export interface UseDynamicFiltersPayload {
  dynamicFilters: FiltersState | null;
  primaryFilter: FilterInformation | null;
  secondaryFilter: FilterInformation | null;
  getFilterInformationForSpecificColumn: (
    columnHeaderValue: string,
  ) => FilterInformation | null;
  onSelect: OnSelect;
  onInject: OnInject;
  resetFiltersToInitialState: () => void;
  isProcessing?: boolean;
  areDynamicFiltersEqual: boolean;
  onSubmit: (dynamicFilters: FiltersState | null) => void;
}

export const areDynamicFiltersEqual = ({
  dynamicFilters,
  submittedDynamicFilters: initialDynamicFilters,
}: {
  dynamicFilters: FiltersState | null;
  submittedDynamicFilters: FiltersState | null;
}) => {
  if (dynamicFilters === null && initialDynamicFilters === null) {
    return true;
  }
  if (dynamicFilters === null || initialDynamicFilters === null) {
    return false;
  }
  for (const filter in dynamicFilters) {
    if (!initialDynamicFilters[filter]) {
      return false;
    }
    const newFilterValues = dynamicFilters[filter].selectedValues;
    const oldFilterValues = initialDynamicFilters[filter].selectedValues;
    if (newFilterValues.length !== oldFilterValues.length) {
      return false;
    }
    for (const index in newFilterValues) {
      if (!oldFilterValues.includes(newFilterValues[index])) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Helper for getting the output format that the extras field in the custom viz API expects from our state
 */
export const getFiltersForExtras = (
  filters: FiltersState | null,
): FilterDefinitionForExtras[] => {
  let dynamicFilters: FilterDefinitionForExtras[] = [];
  if (filters) {
    dynamicFilters = Object.entries(filters).map(
      ([columnHeaderValue, { selectedValues }]) => {
        return {
          columnHeaderValue,
          values: selectedValues,
        };
      },
    );
  }

  return dynamicFilters;
};

export const getFiltersForExtrasWithDefinitions = (
  filterInformation: (FilterInformation | null)[],
): FilterDefinitionForExtras[] => {
  const dynamicFilters: FilterDefinitionForExtras[] = [];
  for (const filter of filterInformation) {
    if (filter) {
      dynamicFilters.push({
        columnHeaderValue: filter.columnHeaderValue,
        values: filter.selectedValues,
      });
    }
  }
  return dynamicFilters;
};

// eslint-disable-next-line complexity
const getFiltersState = ({
  automaticallySelectAll,
  automaticallySelectFirst,
  overridingFilterDefinitionsColumns,
  extraValues,
  value,
  dynamicFilters,
}: {
  automaticallySelectAll?: boolean;
  automaticallySelectFirst?: boolean;
  overridingFilterDefinitionsColumns?: string[];
  extraValues: string[];
  value: {
    filters: FilterDefinitionForExtras[] | null;
  };
  dynamicFilters: FilterDefinitionWithValues[];
}): Nullable<FiltersState> => {
  const filtersState: FiltersState = {};

  const overridingFilterDefinitions = overridingFilterDefinitionsColumns
    ? dynamicFilters?.filter((filter) =>
        overridingFilterDefinitionsColumns.includes(filter.columnHeaderValue),
      )
    : null;

  const dynamicFiltersToUse = overridingFilterDefinitions ?? dynamicFilters;

  for (const filter of dynamicFiltersToUse) {
    const sortedUniqueValues = sortFilterValues([
      ...filter.uniqueValues,
      ...extraValues,
    ]);
    const selectedValues = [];
    const foundFilterDefinition = value?.filters?.find(
      (x) => x.columnHeaderValue === filter.columnHeaderValue,
    );
    if (foundFilterDefinition) {
      selectedValues.push(...foundFilterDefinition.values);
    } else if (automaticallySelectAll !== undefined && automaticallySelectAll) {
      selectedValues.push(...sortedUniqueValues);
    } else if (
      automaticallySelectFirst !== undefined &&
      automaticallySelectFirst &&
      sortedUniqueValues.length > 0
    ) {
      selectedValues.push(sortedUniqueValues[0]);
    }

    filtersState[filter.columnHeaderValue] = {
      columnHeaderValue: filter.columnHeaderValue,
      displayName: filter.displayName,
      values: sortedUniqueValues,
      selectedValues,
    };
  }

  return filtersState;
};

export const useDynamicFilters = ({
  automaticallySelectAll,
  automaticallySelectFirst,
  persistKey,
  includeUnknowns,
  overridingFilterDefinitionsColumns,
  filterSetName,
}: {
  automaticallySelectAll?: boolean;
  automaticallySelectFirst?: boolean;
  persistKey?: string;
  // This option will automatically append "Unknown" to the list of values for each filter. This is used by employee lifecycle.
  includeUnknowns?: boolean;
  overridingFilterDefinitionsColumns?: string[];
  filterSetName: string;
}): UseDynamicFiltersPayload => {
  const [dynamicFilters, internalSetDynamicFilters] =
    useState<FiltersState | null>(null);

  const [submittedDynamicFilters, setSubmittedDynamicFilters] =
    useState<FiltersState | null>(null);

  const [value, setValue] = useSessionStorage({
    key: persistKey ?? 'useDynamicFilters',
    schema: z.object({
      filters: z.array(FilterDefinitionForExtras).nullable(),
    }),
    defaultValue: { filters: null },
  });

  const setDynamicFilters = useCallback(
    (filters: FiltersState | null) => {
      internalSetDynamicFilters(filters);
      setValue(() => {
        return {
          filters: getFiltersForExtras(filters),
        };
      });
    },
    [setValue],
  );

  const observation = useObservation();

  const [didAutoLoad, setDidAutoLoad] = useState(false);

  const filtersWithValues = useMemo(() => {
    return (
      observation.myObservation?.filterSetDefinitionsWithValues.find(
        (x) => x.filterSetName === filterSetName,
      )?.filterDefinitions ?? null
    );
  }, [
    filterSetName,
    observation.myObservation?.filterSetDefinitionsWithValues,
  ]);
  const primaryFilterKey = useMemo(() => {
    return (
      filtersWithValues?.find((filter) => {
        return filter.usageType === 'primary';
      })?.columnHeaderValue ?? null
    );
  }, [filtersWithValues]);

  const secondaryFilterKey = useMemo(() => {
    return (
      filtersWithValues?.find((filter) => {
        return filter.usageType === 'secondary';
      })?.columnHeaderValue ?? null
    );
  }, [filtersWithValues]);

  const primaryFilter = useMemo(() => {
    if (!primaryFilterKey) {
      return null;
    }

    return dynamicFilters?.[primaryFilterKey] ?? null;
  }, [dynamicFilters, primaryFilterKey]);

  const secondaryFilter = useMemo(() => {
    if (!secondaryFilterKey) {
      return null;
    }

    return dynamicFilters?.[secondaryFilterKey] ?? null;
  }, [dynamicFilters, secondaryFilterKey]);

  const extraValues = useMemo(
    () => (includeUnknowns ? ['Unknown'] : []),
    [includeUnknowns],
  );

  const setFiltersToInitialState = useCallback(() => {
    const dynamicFilters = filtersWithValues;

    if (!dynamicFilters) {
      return;
    }

    const filtersState = getFiltersState({
      automaticallySelectAll,
      automaticallySelectFirst,
      value,
      extraValues,
      overridingFilterDefinitionsColumns,
      dynamicFilters,
    });

    internalSetDynamicFilters(filtersState);

    if (automaticallySelectAll || automaticallySelectFirst) {
      // Setting the initial state is treated as a submission since we update the values from outside the normal onSelect mechanism
      setSubmittedDynamicFilters(filtersState);
    }
  }, [
    filtersWithValues,
    automaticallySelectAll,
    automaticallySelectFirst,
    value,
    extraValues,
    overridingFilterDefinitionsColumns,
  ]);

  useEffect(() => {
    if (didAutoLoad) {
      return;
    }

    if (!filtersWithValues) {
      return;
    }

    setFiltersToInitialState();
    setDidAutoLoad(true);
  }, [setFiltersToInitialState, didAutoLoad, filtersWithValues]);

  const internalOnSelect = useCallback(
    ({
      columnHeaderValue,
      selectedValues,
      submitOnChange,
    }: OnSelectParams & { submitOnChange: boolean }) => {
      if (!observation.myObservation) {
        return null;
      }

      const filterDefinition =
        filtersWithValues?.find(
          (filter) => filter.columnHeaderValue === columnHeaderValue,
        ) ?? null;

      if (!filterDefinition) {
        return null;
      }

      internalSetDynamicFilters((prev: FiltersState | null) => {
        const x = prev
          ? {
              ...prev,
              [columnHeaderValue]: {
                ...prev[columnHeaderValue],
                selectedValues,
              },
            }
          : null;

        if (submitOnChange) {
          setSubmittedDynamicFilters(x);
        }
        setValue(() => {
          return {
            filters: getFiltersForExtras(x),
          };
        });
        return x;
      });

      return {
        columnHeaderValue,
        displayName: filterDefinition.displayName,
        values: [...filterDefinition.uniqueValues, ...extraValues],
        selectedValues,
      };
    },
    [observation.myObservation, filtersWithValues, extraValues, setValue],
  );

  const onSelect: OnSelect = useCallback(
    (params) => {
      return internalOnSelect({ submitOnChange: false, ...params });
    },
    [internalOnSelect],
  );

  const onSubmit = useCallback(
    (dynamicFilters: FiltersState) => {
      setSubmittedDynamicFilters(dynamicFilters);
    },
    [setSubmittedDynamicFilters],
  );

  const onInject: OnInject = useCallback(
    (filterInformation) => {
      for (const filter of filterInformation) {
        internalOnSelect({
          columnHeaderValue: filter.columnHeaderValue,
          selectedValues: filter.values,
          // Injecting is treated as a submission since we update the values from outside the normal onSelect mechanism
          submitOnChange: true,
        });
      }
    },
    [internalOnSelect],
  );

  const getFilterInformationForSpecificColumn = useCallback(
    (columnHeaderValue: string) => {
      if (primaryFilter?.columnHeaderValue === columnHeaderValue) {
        return primaryFilter;
      }

      if (secondaryFilter?.columnHeaderValue === columnHeaderValue) {
        return secondaryFilter;
      }

      return dynamicFilters?.[columnHeaderValue] ?? null;
    },
    [dynamicFilters, primaryFilter, secondaryFilter],
  );

  const uniqueValuesMap = useMemo(() => {
    return (
      observation?.myObservation?.uniqueValuesMapPerSet.find(
        (x) => x.filterSetName === filterSetName,
      )?.uniqueValuesMap ?? null
    );
  }, [filterSetName, observation?.myObservation?.uniqueValuesMapPerSet]);
  return useMemo(() => {
    const inputs: Record<string, string[]> = {};
    const uniqueColumnMap = uniqueValuesMap ?? [];
    const dynamicFilterEntries = Object.entries(dynamicFilters ?? {});

    for (const [h, f] of dynamicFilterEntries) {
      inputs[h] = f.selectedValues;
    }

    const graph = buildDependencyGraph(filtersWithValues ?? []);
    const newFilters: { [columnHeaderValue: string]: FilterInformation } = {};

    for (const [header, filterInformation] of dynamicFilterEntries) {
      const determineOptionsAvailableResult = determineOptionsAvailable({
        uniqueColumnMap,
        header,
        inputs,
        fallbackUniqueValues: filterInformation.values,
        graph,
      });
      const optionsAvailable = [
        ...determineOptionsAvailableResult,
        ...extraValues,
      ];

      const sortedValues = sortFilterValues(optionsAvailable);
      newFilters[header] = {
        displayName: filterInformation.displayName,
        values: sortedValues,
        selectedValues: filterInformation.selectedValues,
        columnHeaderValue: header,
      };
    }

    return {
      dynamicFilters: newFilters,
      getFilterInformationForSpecificColumn,
      primaryFilter,
      secondaryFilter,
      onSelect,
      onInject,
      onSubmit,
      resetFiltersToInitialState: setFiltersToInitialState,
      areDynamicFiltersEqual: areDynamicFiltersEqual({
        dynamicFilters,
        submittedDynamicFilters,
      }),
    };
  }, [
    uniqueValuesMap,
    dynamicFilters,
    filtersWithValues,
    getFilterInformationForSpecificColumn,
    primaryFilter,
    secondaryFilter,
    onSelect,
    onInject,
    onSubmit,
    setFiltersToInitialState,
    submittedDynamicFilters,
    extraValues,
  ]);
};
